import {
  Container,
  Grid,
  LinearProgress,
  TextField,
  Tooltip,
} from '@mui/material';
import { GroupsContainer, OutlinedIconButton } from 'shared/components/styled';
import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Root } from './styled';
import VirtualizedView, { RowGrid } from 'shared/components/VirtualizedView';
import GlobalResource from 'shared/components/GlobalResources';
import { columnTypes } from 'shared/components/tables';
import { debounce, uniq, uniqBy } from 'lodash';
import { searchToQuery } from 'shared/components/makeNav';
import { useHistory, useLocation, Route, Redirect } from 'react-router-dom';
import {
  useAllServiceCollections,
  useIndicatorStages,
  useAllIndicators,
} from 'shared/apollo';
import { useDonorsQuery } from '../ServiceCollections/queries';

import { useServiceCollectionsQuery } from '../ServiceCollections/useServiceCollectionsQuery';
import { useNewService } from '../ServiceDrawer/useNewService';
import { useUpdateService } from '../ServiceDrawer/useUpdateService';
import { useDeleteService } from '../ServiceDrawer/useDeleteService';
import { DetailPopup, ConfirmationDialog } from 'shared/components';
import ServiceDrawer from '../ServiceDrawer/ServiceDrawer';
import { SearchInputContainer } from 'shared/components/SearchInput';
import get from 'lodash/get';
import { tableCols } from './constants';
import useHash from 'shared/utils/useHash';
import { useServicesQuery } from './useServicesQuery';
import { useUser } from 'shared/auth';
import { useNewServiceToPortfolioMutation, useDeleteService2portfolioMutation } from 'shared/apollo';
import { forEach} from 'lodash';

const baseUrl = '/account/impactModels';
const storageKey = 'servicesUrl';

export default function Services() {
  const user = useUser();
  const effectiveUser = user?.effectiveUser;
  const userId = effectiveUser?.attributes['custom:user_id'];
  const defaultUrl = userId && localStorage.getItem(`User${userId}.${storageKey}`);
  const currentUrl = window.location.pathname + window.location.search;

  return (
    <Route
      path={baseUrl}
      component={ServicesMain}
    >
      {defaultUrl && defaultUrl !== currentUrl &&
        <Redirect to={defaultUrl} />}
    </Route>
  );
}

function ServicesMain() {
  const { data, isLoading, refresh } = useServicesQuery();
  const { data: newService, isLoading: isNewLoading, run: runNew } = useNewService();
  const { isLoading: isUpdateLoading, run: runUpdate } = useUpdateService();
  const { isLoading: isDeleteLoading, run: runDelete } = useDeleteService();
  const { data: collections } = useServiceCollectionsQuery();
  const { data: indicatorStages } = useIndicatorStages();
  const { data: indicators } = useAllIndicators();
  const { data: donors, isLoading: isDonorsLoading } = useDonorsQuery();
  const user = useUser();
  const effectiveUser = user?.effectiveUser;
  const userId = effectiveUser?.attributes['custom:user_id'];
  const { search: searchParams, pathname } = useLocation();
  const [selection, setSelection] = useState([]);
  const [selectedService, setSelectedService] = useState();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState();
  const { mutateNewService2Portfolio: newPort } = useNewServiceToPortfolioMutation();
  const { mutateDeleteService2portfolio: deletePort } = useDeleteService2portfolioMutation();


  const virtualized = useRef();
  useEffect(() => {
    if (!virtualized) {
      return;
    }
    virtualized.current?.clearSelection();
  }, []);

  const onSave = (form, addedPortfolios, removedPortfolios) => {
    setDrawerOpen(false);
    const mutationFunc = form.id ? runUpdate : runNew;
    mutationFunc(form, (response) => {
      refresh();
      forEach(addedPortfolios, (id) => {
        newPort({ serviceId: response, portfolioId: id });
      });
      forEach(removedPortfolios, (id) => {
        deletePort({ serviceId: response, portfolioId: id });
      });
    });

  };

  const onClose = () => {
    setSelectedService();
    setDrawerOpen(false);
  };

  const onAddNew = () => {
    setSelectedService();
    setDrawerOpen(true);
  };

  const handleViewDetails = (selected) => {
    setSelectedService(selected.dbId);
    setDrawerOpen(true);
  };

  const onRequestDelete = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = (selected) => {
    setIdToDelete(selected.map(item => item.dbId));
    setDeleteDialogOpen(true);
  };

  const onDelete = () => {
    setDrawerOpen(false);
    setDeleteDialogOpen(false);
    runDelete(({ id: idToDelete }), () => {
      setSelectedService();
      virtualized?.current?.clearSelection();
      refresh();
    });
  };

  const handleEdit = useCallback(() => {
    if (selection) {
      if (selection.length === 0) {
        setSelectedService();
      } else if (selection.length === 1) {
        setSelectedService(selection[0]?.dbId);
        setDrawerOpen(true);
      } else {
        alert('Cannot edit multiple items');
      }
    }
  }, [selection]);

  useEffect(() => {
    const key = `User${userId}.${storageKey}`;
    localStorage.setItem(key, `${pathname}${searchParams}`);
  }, [searchParams, pathname, userId]);
  const { push } = useHistory();
  const handleClearPageSettings = useCallback(() => {
    const key = `User${userId}.${storageKey}`;
    localStorage.removeItem(key);
    push(baseUrl);
  }, [userId, push]);

  const [filter, setFilter] = useState();

  const groups = data
    ?.filter(obj => !filter || matchQuickFilter(obj, filter));
  const [columns, setColumns] = useState(tableCols);
  const hash = useHash() || '';
  const hashParts = hash.substring(1).split('/').filter(part => part);

  const handleRenderRow = useCallback(({
    value,
    columns,
    style, // Style object to be applied to row (to position it)
    selectedRows,
    setSelectedRows,
    hasCheckBox,
    isLastRow,
  }) => {
    return (
      <RowGrid
        {...{
          style, value, columns, columnTypes,
          selectedRows, setSelectedRows, hasCheckBox,
          isLastRow,
        }}
        keyField="dbId"
      />
    );
  }, [columns]);

  const searchOptions = data && uniq(
    quickFilterFields
      .reduce((all, field) => [
        ...all,
        ...data.map(row => get(row, field))
      ], [])
  )
    .filter(option => option)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const handleSearchFocus = useCallback(() => {
    setIsSearchFocused(true);
  }, []);
  const handleSearchBlur = useCallback(() => {
    setIsSearchFocused(false);
  }, []);
  const debouncedSetFilter = useMemo(() => debounce(setFilter, 500), []);
  const handleSearchInput = useCallback((event, value) => {
    debouncedSetFilter(value);
  }, [debouncedSetFilter]);
  const handleSearchRenderInput = useCallback(params => (
    <SearchInputContainer maxWidth={400}>
      <TextField
        placeholder={isSearchFocused ? undefined : 'Search Impact Models'}
        onFocus={handleSearchFocus}
        onBlur={handleSearchBlur}
        {...params}
      />
    </SearchInputContainer>
  ), [handleSearchInput, isSearchFocused]);

  return (<Fragment>
    {/* <Rows
      {...{
        isLoading: isLoading,
        columns, virtualized,
        searchOptions, handleSearchInput, 
        handleSearchRenderInput, groups,
        handleClearPageSettings, refresh,
        handleRenderRow, setSelection,
        selection, onAddNew, handleEdit
      }}
    /> */}
    <GlobalResource
      resource='Impact Model'
      fields={['dbId', 'name', 'description']}
      fieldTitles={['ID', 'Name', 'Description']}
      onAddNew={onAddNew}
      handleViewDetails={handleViewDetails}
      {...{
        isLoading, data, handleDelete
      }}
    />
    <DetailPopup title="Impact Model" open={drawerOpen} onClose={onClose} insideCard={false} width={'450px'}>
      <Container maxWidth={false}>
        <ServiceDrawer
          onSave={onSave}
          dbId={selectedService}
          collections={collections}
          donors={donors}
          donorLoad={isDonorsLoading}
          indicatorStages={indicatorStages}
          indicators={indicators}
          isNewLoading={isNewLoading}
          isUpdateLoading={isUpdateLoading}
          isDeleteLoading={isDeleteLoading}
          requestDelete={onRequestDelete}
        />
      </Container>
    </DetailPopup>
    <ConfirmationDialog
      openDialog={deleteDialogOpen}
      onCloseDialog={() => setDeleteDialogOpen(false)}
      actionButtonOne={onDelete}
      actionButtonTwo={() => setDeleteDialogOpen(false)}
      textButtonOne="Delete"
      textButtonTwo="Cancel"
      title="Are you sure you want to delete the impact model selected?"
    />
  </Fragment>);
}

// const Rows = memo(({
//   isLoading, searchOptions, handleSearchInput,
//   handleSearchRenderInput, groups,
//   columns, virtualized,
//   handleClearPageSettings,
//   handleRenderRow, setSelection,
//   selection, onAddNew, handleEdit
// }) => {
//   const [filters, setFilters] = useState([])
//   const handleFilterValue = useCallback(() => {
//     try {
//       const { search } = window.location
//       const query = searchToQuery(search)
//       setFilters([
//         'name', 'stgName', 'definition', 'guidance',
//         'benchmarkType', 'measurementType',
//       ]
//         .filter(name => query[name])
//         .map(name => [name, query[name]])
//         .reduce((all, [name, values]) => [
//           ...all,
//           ...JSON.parse(values)
//             .map(value => ({ filter: name, value }))
//         ], [])
//       )
//     } catch (err) {
//       alert(err)
//     }
//   }, [])
//   const filtered = groups
//     ?.filter(obj => !filters.length || filters
//       .some(({ filter, value }) => get(obj, filter) === value))

//   return (
//     <HomePage noPadding fullHeight>
//       <PageTop>
//         {isLoading && <LinearProgress variant="query" />}
//         <Grid
//           container
//           direction="row"
//           alignItems="center"
//           xs={12}
//         >
//           <Grid item xs={3}>
//             <Autocomplete
//               id="indicator-search"
//               freeSolo
//               options={searchOptions}
//               renderInput={handleSearchRenderInput}
//               onInputChange={handleSearchInput}
//             />
//           </Grid>
//           <Grid item xs={7}>
//             <Tooltip title="Reset filters" arrow>
//               <OutlinedIconButton
//                 onClick={handleClearPageSettings}
//               >
//                 <ResetIcon />
//               </OutlinedIconButton>
//             </Tooltip>
//             <ButtonSpacer withBorder />
//             {selection && selection.length > 0 && (
//               <HomeButton onClick={handleEdit} disabled={(selection.length > 1)}>
//                 Edit
//               </HomeButton>
//             )}
//           </Grid>
//           <Grid item xs={2} container justifyContent="flex-end">
//             <MainButton
//               onClick={onAddNew}
//               startIcon={<AddIcon />}
//             >
//               Add Impact Model
//             </MainButton>
//           </Grid>
//         </Grid>
//         <GlobalResourceDropDown value="Impact Models" />
//       </PageTop>
//       <Root>
//         <GroupsContainer>
//           <VirtualizedView
//             ref={virtualized}
//             view="grid"
//             groups={filtered}
//             unfilteredGroups={groups}
//             {...{ columns, isLoading }}
//             onRenderRow={handleRenderRow}
//             onSelectionChanged={setSelection}
//             onFilterValue={handleFilterValue}
//           />
//         </GroupsContainer>
//       </Root>
//     </HomePage>
//   )
// })


const quickFilterFields = [
  'name',
  'stgName',
];

function matchQuickFilter(obj, filter) {
  filter = filter.toLowerCase();
  return quickFilterFields
    .some(field => String(get(obj, field)).toLowerCase().includes(filter));
}
